import { ClipboardCopy, CornerDownLeft, Link2 } from "lucide-react";
import { Button } from "../ui/button";
import { TooltipProvider } from "../ui/tooltip";
import { HintTooltip } from "./HintTooltip";
import { exerciseTypeIcon } from "./exerciseIcons";
import { isSharedReadonly } from "./ReferenceWeekView";
import {
	EXERCISE_TYPES,
	formatExerciseDisplayName,
	formatExercisePrescription,
	formatExerciseSetRowLines,
	getNormalizedDayExercises,
} from "../../lib/programBuilderUtils";
import {
	hasExerciseDragPayload,
	setExerciseDragPayload,
} from "../../lib/programClipboard";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Week overview: the selected week's days side by side, one compact column
 * per day (docs/program-builder-season-strip/ANALYSIS.md §6, Fase 3).
 *
 * This is a second *view* of the same week, not a second editor. It is
 * read-only on purpose: a click on a day hands over to the vertical editor
 * scrolled to that day. The grid is `auto-fill`, so it degrades by itself:
 * 7 columns on a large monitor, 4-5 on a laptop, 2-3 on a tablet. Columns
 * never scroll on their own (they grow), so drops from the reference panel
 * and the page's drag auto-scroll keep working exactly as in the editor.
 *
 * Unlike the reference panel's one-line rows, here an exercise takes the
 * lines it needs: name, then the full prescription, then set rows and notes.
 * The point of the view is reading the week without opening anything.
 */

function OverviewExerciseRow({ exercise, dayIndex, exerciseIndex, onCopy }) {
	const { t } = useLanguage();
	const exerciseType = exercise?.exercise_type || "strength";
	const TypeIcon = exerciseTypeIcon(exerciseType);
	const typeColor =
		EXERCISE_TYPES.find((item) => item.value === exerciseType)?.color ||
		"text-volt";
	const name = formatExerciseDisplayName(exercise);
	const prescription = formatExercisePrescription(exercise, t);
	const setRowLines = formatExerciseSetRowLines(exercise);
	const notes = String(exercise?.notes || "").trim();
	const readonly = isSharedReadonly(exercise);

	return (
		<div
			draggable={!readonly}
			onDragStart={(event) => {
				if (readonly) return;
				setExerciseDragPayload(event.dataTransfer, [{ ...exercise }]);
			}}
			className={`group border-b border-border/40 last:border-b-0 px-2 py-1.5 space-y-0.5 hover:bg-secondary/40 ${
				readonly ? "" : "cursor-grab active:cursor-grabbing"
			}`}
		>
			<div className="flex items-start gap-1.5">
				<TypeIcon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${typeColor}`} />
				<span className="flex-1 min-w-0 text-xs text-foreground leading-snug break-words">
					{name}
				</span>
				{readonly ? (
					<HintTooltip label={t("pbReferenceSharedReadonly")}>
						<span className="shrink-0 inline-flex mt-0.5">
							<Link2 className="w-3 h-3 text-amber-400/80" />
						</span>
					</HintTooltip>
				) : (
					<HintTooltip label={t("pbReferenceCopyExercise")}>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							onClick={() => onCopy(dayIndex, exerciseIndex)}
							aria-label={t("pbReferenceCopyExercise")}
							className="h-5 w-5 -mt-0.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-volt"
						>
							<ClipboardCopy className="w-3 h-3" />
						</Button>
					</HintTooltip>
				)}
			</div>
			{prescription && (
				<p className="pl-5 font-mono text-[11px] leading-snug text-muted-foreground whitespace-pre-wrap break-words">
					{prescription}
				</p>
			)}
			{setRowLines.length > 1 &&
				setRowLines.map((line, index) => (
					<p
						key={index}
						className="pl-5 font-mono text-[11px] leading-snug text-muted-foreground/80 break-words"
					>
						{line}
					</p>
				))}
			{notes && (
				<p className="pl-5 text-[11px] leading-snug text-muted-foreground/70 italic whitespace-pre-wrap break-words line-clamp-3">
					{notes}
				</p>
			)}
		</div>
	);
}

export function WeekOverview({
	days,
	dayLabel,
	dayDateLabel,
	onOpenDay,
	onCopyExercise,
	dropDayIndex,
	onDropDayIndexChange,
	onDropExercises,
}) {
	const { t } = useLanguage();
	const list = days || [];

	return (
		<TooltipProvider>
			<div className="space-y-2" data-testid="week-overview">
				<p className="text-[11px] text-muted-foreground">
					{t("pbWeekOverviewHint")}
				</p>
				<div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(250px,1fr))] items-start">
					{list.map((day, dayIndex) => {
						const exercises = getNormalizedDayExercises(day);
						const isDropTarget = dropDayIndex === dayIndex;
						return (
							<div
								key={day?.day_id || dayIndex}
								data-testid={`overview-day-${dayIndex}`}
								className={`rounded-sm border bg-card min-w-0 transition-colors ${
									isDropTarget
										? "border-volt ring-2 ring-volt/60"
										: "border-border"
								}`}
								// Same drop contract as the editor's day card: only payloads
								// carrying the exercise MIME type are ours.
								onDragOver={(event) => {
									if (!hasExerciseDragPayload(event.dataTransfer)) return;
									event.preventDefault();
									event.dataTransfer.dropEffect = "copy";
									if (dropDayIndex !== dayIndex) onDropDayIndexChange(dayIndex);
								}}
								onDragLeave={(event) => {
									if (event.currentTarget.contains(event.relatedTarget)) return;
									if (dropDayIndex === dayIndex) onDropDayIndexChange(null);
								}}
								onDrop={(event) => onDropExercises(event, dayIndex)}
							>
								<button
									type="button"
									onClick={() => onOpenDay(dayIndex)}
									className="w-full text-left px-2 py-2 border-b border-border bg-secondary/30 hover:bg-volt/10 rounded-t-sm group"
									title={t("pbWeekOverviewOpenDay")}
								>
									<div className="flex items-center gap-2">
										<div className="flex-1 min-w-0">
											<p className="text-xs font-heading truncate">
												{dayLabel(day, dayIndex)}
											</p>
											{dayDateLabel(day) && (
												<p className="text-[10px] text-muted-foreground truncate">
													{dayDateLabel(day)}
												</p>
											)}
										</div>
										<span className="shrink-0 text-[10px] text-muted-foreground font-mono">
											{exercises.length}
										</span>
										<CornerDownLeft className="w-3 h-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
									</div>
								</button>

								{exercises.length === 0 ? (
									<p className="px-2 py-3 text-[11px] text-muted-foreground/70 text-center">
										{t("pbReferenceEmptyDay")}
									</p>
								) : (
									exercises.map((exercise, exerciseIndex) => (
										<OverviewExerciseRow
											key={exercise?.exercise_id || exerciseIndex}
											exercise={exercise}
											dayIndex={dayIndex}
											exerciseIndex={exerciseIndex}
											onCopy={onCopyExercise}
										/>
									))
								)}

								<div className="px-1 py-1 border-t border-border/40 flex justify-end">
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() => onOpenDay(dayIndex)}
										className="h-6 px-2 rounded-sm text-[11px] text-muted-foreground hover:text-volt"
									>
										{t("pbWeekOverviewOpenDay")}
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</TooltipProvider>
	);
}
